import EmberObject from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { getModelNameForEmberModelInstance, md5Hash, getLoggerNameForEmberObject } from "../utils/misc";

/**
 * Default time in seconds to keep in cache
 *
 * @type {number}
 */
const DEFAULT_TIMEOUT_SECONDS = 300; // 5 minutes

/**
 * The common timeouts for the cache
 *
 * @type {Object}
 */
export const APP_CACHE__COMMON_TIMEOUTS = {
  ONE_HOUR_SECONDS: 3600,
  ONE_DAY_SECONDS: 86400,
};

/**
 * Service to cache model objects and lists of objects bound to a particular API call.
 *
 * Because a model can be in different states based on the 'includes' loaded with the object,
 * this is a service to keep track of that and cache with respect to the 'includes' fetched and
 * NOT just the model id.
 *
 * e.g. subsequent calls to GET /users/1234?include=location,last-location and GET /users/1234?include=location would
 * NOT hit the cache since the `includes` are not the same.  We could have done something more
 * elaborate but wanted to keep it relatively simple.
 */
export default Service.extend({
  /**
   * Register services
   */
  store: service(),
  modelCacheExpiryMap: EmberObject.create({}),

  /**
   * @inheritDoc
   */
  init() {
    this._super(...arguments);
    window['SH_APP_CACHE'] = this.get('modelCacheExpiryMap');
  },

  /**
   * Get a cached version of the model given a set of includes for a request or return null
   * if not found.
   *
   * @param {String} modelName
   * @param {String} modelId
   * @param {Array<String>} opt_includes
   * @return {null|*}
   */
  getCachedModelForRequest(modelName, modelId, opt_includes = []) {
    let cacheKey        = this._getCacheKey(modelName, modelId, opt_includes),
      modelExpiryMap    = this.get('modelCacheExpiryMap'),
      cachedObject      = modelExpiryMap.get(cacheKey);

    if (!cachedObject) {
      // cache miss
      return null;
    }

    let cacheTimeoutSeconds = cachedObject.get('cacheTimeoutSeconds') * 1000,
      cacheSetAt = cachedObject.get('cacheSetAt');

    if (Date.now() - cacheSetAt > cacheTimeoutSeconds) {
      // cache expired
      modelExpiryMap.set(cacheKey, null);
      return null;
    }

    // cache hit
    return cachedObject.get('cacheValue');
  },

  /**
   * Get a cached response for an array of models.
   *
   * @param {EmberObject} emberObjInstance    This will typically be the command-service that is fetching the models.
   *                                          Can be any ember style object though.  Will be converted to a static string
   *                                          representing the model instead of per instance.
   * @param {String} functionName             The name of the function to use as a unique key with the object instance.
   * @param {Object} callerArguments          This should be the 'arguments' from the calling function.  This is useful
   *                                          to know if we have includes or other params that will make this request
   *                                          unique.
   * @return {null|Ember.A<*>}                return null if a miss or the array of ember models
   */
  getCachedModelArrayForRequest(emberObjInstance, functionName, callerArguments) {
    let cacheKey        = this._getCacheKeyModelArray(emberObjInstance, functionName, callerArguments),
      modelExpiryMap    = this.get('modelCacheExpiryMap'),
      cachedObject      = modelExpiryMap.get(cacheKey);

    if (!cachedObject) {
      // cache miss
      return null;
    }

    let cacheTimeoutSeconds = cachedObject.get('cacheTimeoutSeconds') * 1000,
      cacheSetAt = cachedObject.get('cacheSetAt');

    if (Date.now() - cacheSetAt > cacheTimeoutSeconds) {
      // cache expired
      modelExpiryMap.set(cacheKey, null);
      return null;
    }

    // cache hit

    return cachedObject.get('cacheValue');
  },

  /**
   * TODO ---- UNTESTED
   *
   * This will go through all of our cached models (ONLY INDIVIDUAL CACHED MODELS, ARRAYS NOT INCLUDED)
   * and remove any where the name/model ids match
   *
   * @param {String} modelName
   * @param {String[]} modelIds
   */
  invalidateCachedModel(modelName, modelIds) {
    let modelExpiryMap = this.get('modelCacheExpiryMap');
    Object.keys(modelExpiryMap).forEach(cacheKey => {
      let cachedModel = modelExpiryMap.get(cacheKey).get('cachedValue');

      if (modelName === getModelNameForEmberModelInstance(cachedModel) &&
      modelIds.includes(cachedModel.get('id'))) {
        modelExpiryMap.set(cacheKey, null);
      }
    });
  },

  /**
   * Set a model in the cache for the given `included` relationships that were loaded.
   *
   * @param {*} model
   * @param {String} modelName
   * @param {Array<String>} opt_includes
   * @param {Number=} opt_timeoutInSeconds
   */
  setCachedModelForRequest(model, modelName, opt_includes = [], opt_timeoutInSeconds = DEFAULT_TIMEOUT_SECONDS) {
    let modelExpiryMap    = this.get('modelCacheExpiryMap'),
      cachedObject        = EmberObject.create({
        cacheTimeoutSeconds: opt_timeoutInSeconds,
        cacheSetAt: Date.now(),
        cacheValue: model
      });

    modelExpiryMap.set(this._getCacheKey(modelName, model.get('id'), opt_includes), cachedObject);
  },

  /**
   * Get a cached response for an array of models.
   *
   * @param {EmberObject} emberObjInstance    This will typically be the command-service that is fetching the models.
   *                                          Can be any ember style object though.  Will be converted to a static string
   *                                          representing the model instead of per instance.
   * @param {String} functionName             The name of the function to use as a unique key with the object instance.
   * @param {Object} callerArguments          This should be the 'arguments' from the calling function.  This is useful
   *                                          to know if we have includes or other params that will make this request
   *                                          unique.
   * @param {*} objectToCache                 Object that will go in the cache.
   * @param {Number=} opt_timeoutInSeconds    Number in seconds of how long to keep in cache, or DEFAULT_TIMEOUT_SECONDS
   * @return {null|Ember.A<*>}                return null if a miss or the array of ember models
   */
  setCachedModelArrayForRequest(emberObjInstance, functionName, callerArguments, objectToCache, opt_timeoutInSeconds = DEFAULT_TIMEOUT_SECONDS) {
    let modelExpiryMap    = this.get('modelCacheExpiryMap'),
      cachedObject        = EmberObject.create({
        cacheTimeoutSeconds: opt_timeoutInSeconds,
        cacheSetAt: Date.now(),
        cacheValue: objectToCache
      }),
      cacheKey            = this._getCacheKeyModelArray(emberObjInstance, functionName, callerArguments);

    modelExpiryMap.set(cacheKey, cachedObject);
  },

  /**
   * Get a hash key for the fields that will make this cache hit unique
   *
   * @param {String} modelName
   * @param {String} modelId
   * @param {Array<String>} includes
   * @return {String}
   * @private
   */
  _getCacheKey(modelName, modelId, includes) {
    let cacheString     = modelName + modelId + includes.join(',');
    return md5Hash(cacheString);
  },

  /**
   * Get a cached response for an array of models.
   *
   * @param {EmberObject} emberObjInstance    This will typically be the command-service that is fetching the models.
   *                                          Can be any ember style object though.  Will be converted to a static string
   *                                          representing the model instead of per instance.
   * @param {String} functionName             The name of the function to use as a unique key with the object instance.
   * @param {Object} callerArguments          This should be the 'arguments' from the calling function.  This is useful
   *                                          to know if we have includes or other params that will make this request
   *                                          unique.
   * @return {String}                         md5 hash of the arguments that would make this request unique.
   * @private
   */
  _getCacheKeyModelArray(emberObjInstance, functionName, callerArguments) {
    let instanceName    = getLoggerNameForEmberObject(emberObjInstance),
      argumentsStringBuilder = [];

    Object.values(callerArguments).forEach(callerArg => {
      let modelType     = getModelNameForEmberModelInstance(callerArg),
        modelId         = modelType ? callerArg.get('id') : null;

      if (modelType && modelId) {
        // ensure this is valid
        let model = this.get('store').peekRecord(modelType, modelId);
        if (!model) {
          throw new Error('object type not supported ' + callerArg);
        }

        argumentsStringBuilder.push(`${modelType}-${modelId}`);
      } else if (Array.isArray(callerArg) ||
      typeof callerArg === 'number' ||
      typeof callerArg === 'string') {
        argumentsStringBuilder.push(callerArg.toString());
      } else if (callerArg === undefined) {
        argumentsStringBuilder.push('undefined');
      } else if (callerArg === null) {
        argumentsStringBuilder.push('null');
      } else if (Object.keys(callerArg)) {
        let keyVals = [];

        Object.keys(callerArg).forEach(key => {
          // make sure all key/vals are able to be cached
          let cacheKeyString = this._getPrimitiveCacheKeyString(key),
            cacheValueString = this._getPrimitiveCacheKeyString(callerArg[key]);

          if (!cacheKeyString || !cacheValueString) {
            throw new Error('object type not supported for app cache model array! ' + callerArg);
          }

          keyVals.push(cacheKeyString + cacheValueString);
        });

        argumentsStringBuilder.push(keyVals.join(''));
      } else {
        throw new Error('object type not supported for app cache model array! ' + callerArg);
      }
    });

    let cacheString       = instanceName + functionName + argumentsStringBuilder.join('');
    return md5Hash(cacheString);
  },

  /**
   * Given a value, return it's toStringed value.
   *
   * Thsi will return undefined when it is not supported.
   *
   * @param {*} value
   * @return {string|undefined}
   * @private
   */
  _getPrimitiveCacheKeyString(value) {
    if (Array.isArray(value) ||
    typeof value === 'number' ||
    typeof value === 'string') {
      return value.toString();
    } else if (value === undefined) {
      return 'undefined';
    } else if (value === null) {
      return 'null';
    }

    return undefined;
  }
});
